// GroupsSUITE shared UI building blocks — ported from GroupsLINK's gl-shared.jsx
// and adapted to GroupsMEDIA's esbuild/ESM setup (import React). Only the parts
// used by the Domain/Channel screens. The module accent runs through the
// CSS custom property var(--accent) (#ec4899 in GroupsMEDIA).
import React from "react"

const ICON_PATHS = {
  plus:      '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
  link:      '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  chevron:   '<polyline points="9 18 15 12 9 6"/>',
  edit:      '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>',
  arrow_left:'<line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>',
  image:     '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>',
  search:    '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>',
  x:         '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
}

export function Icon({ name, size = 14, color = "currentColor" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
      dangerouslySetInnerHTML={{ __html: ICON_PATHS[name] || "" }}
    />
  )
}

const BADGE_TONES = {
  neutral: { bg: "#f1f5f9", fg: "#4b5563", bd: "#e5e7eb" },
  success: { bg: "#f0fdf4", fg: "#15803d", bd: "#bbf7d0" },
  accent:  { bg: "var(--accent)", fg: "var(--accent-ink)", bd: "var(--accent)" },
}

export function Badge({ tone = "neutral", dot = false, children }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral
  return (
    <span style={{
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
      display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px",
      borderRadius: 9999, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {dot && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", flexShrink: 0 }} />}
      {children}
    </span>
  )
}

const BTN_TONES = {
  primary:   { bg: "var(--primary)", fg: "var(--primary-ink)", bd: "var(--primary)" },
  secondary: { bg: "#fff", fg: "#374151", bd: "#d1d5db" },
  accent:    { bg: "var(--accent)", fg: "var(--accent-ink)", bd: "var(--accent)" },
}

export function Btn({ variant = "secondary", href, onClick, icon, disabled = false, title, children }) {
  const t = BTN_TONES[variant] || BTN_TONES.secondary
  const style = {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px",
    borderRadius: 6, border: `1px solid ${t.bd}`, background: t.bg, color: t.fg,
    fontSize: 14, fontWeight: 500, fontFamily: "var(--font-sans)",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
    textDecoration: "none", whiteSpace: "nowrap",
  }
  const inner = (
    <>
      {icon && <Icon name={icon} size={14} color={t.fg} />}
      {children}
    </>
  )
  if (href && !disabled) {
    return <a href={href} style={style} title={title}>{inner}</a>
  }
  return (
    <button type="button" style={style} disabled={disabled} title={title} onClick={onClick} aria-disabled={disabled}>
      {inner}
    </button>
  )
}

export function Card({ children, style }) {
  return (
    <div style={{
      background: "#fff", border: "1px solid var(--border)", borderRadius: 12,
      padding: 18, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", ...style,
    }}>
      {children}
    </div>
  )
}
